import React, { FC, useMemo, useState } from 'react'
import moment from 'moment'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import {
  IAs2Certificate,
  IAs2ConnectionConfig,
  ICreateAs2CertificateInput,
} from 'types/graphqlTypes'
import {
  brandColors,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'transfix-ui'
import { AS2CertificateDetails } from './AS2CertificateDetails'
import { AS2CertificatesTableRow } from './AS2CertificatesTableRow'
import { certificateValidity } from './certificateValidity'
import { useAs2Certificate } from './hooks/useAs2Certificate'
import { As2CertificateModal } from './modals/As2CertificateModal/As2CertificateModal'

interface IAS2CertificatesTableProps {
  configDetails: IAs2ConnectionConfig
  displayUserFeedback(message: string, level: 'error' | 'info'): void
}

const useAS2ConnectionConfigCertificatesTableStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: 'none',
    boxShadow: 'none',
  },
  table: {
    background: 'transparent',
    marginTop: theme.spacing(1),
    '& thead': {
      background: 'transparent',
    },
    '& th:first-child, td:first-child': {
      paddingLeft: 0,
    },
    '& td': {
      borderBottom: `1px solid ${brandColors.coolGray3}`,
    },
  },
  text: {
    color: 'red',
  },
}))

export const AS2CertificatesTable: FC<IAS2CertificatesTableProps> = ({
  configDetails,
  displayUserFeedback,
}) => {
  const { as2Certificates, id } = configDetails
  const [as2Certificate, setAs2Certificate] = useState<IAs2Certificate>()
  const [isOpen, setOpenModal] = useState<boolean>()
  const closeHandler = () => setOpenModal(false)

  const classes = useAS2ConnectionConfigCertificatesTableStyles()

  const clickHandler = () => {
    setOpenModal(true)
  }

  const rowClickHandler = (certificate: IAs2Certificate) => {
    setAs2Certificate(certificate)
  }

  const backClickHandler = () => {
    setAs2Certificate(undefined)
  }

  const { createAs2Certificate } = useAs2Certificate({
    displayUserFeedback,
  })

  const submitHandler = (data: ICreateAs2CertificateInput) => {
    createAs2Certificate({
      body: data?.body,
      as2ConnectionConfigId: +id,
    })
  }

  const sortedCertificateList = useMemo(() => {
    return [...as2Certificates].sort(
      (a, b) => moment(a.notAfter).valueOf() - moment(b.notAfter).valueOf()
    )
  }, [as2Certificates])

  if (!as2Certificates) {
    return null
  }

  return (
    <Box padding={3}>
      {as2Certificate ? (
        <AS2CertificateDetails
          as2ConnectionConfigId={id}
          as2Certificate={as2Certificate}
          onBackClick={backClickHandler}
          displayUserFeedback={displayUserFeedback}
        />
      ) : (
        <>
          <Grid container direction='row' justifyContent='space-between' alignItems='center'>
            <Grid item xs={12} md='auto'>
              <Typography variant='h4' display='inline'>
                Certificates
              </Typography>
            </Grid>
            <Grid item xs={12} md='auto'>
              <Button
                color='secondary'
                size='small'
                data-test='add-as2-certificate-button'
                onClick={clickHandler}>
                Add New
              </Button>
            </Grid>
          </Grid>
          <Card className={classes.card}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow tableCellType='th'>
                  <TableCell className='tc'>ID</TableCell>
                  <TableCell className='tc'>Created At</TableCell>
                  <TableCell className='tc'>Expires At</TableCell>
                  <TableCell className='tc'>Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCertificateList && sortedCertificateList.length > 0 ? (
                  sortedCertificateList.map(as2Certificate => {
                    const validity = certificateValidity(as2Certificate.notAfter)
                    const rowClickHandlerCallback = () => rowClickHandler(as2Certificate)
                    return (
                      <AS2CertificatesTableRow
                        key={as2Certificate.id}
                        as2Certificate={as2Certificate}
                        validity={validity}
                        onRowClick={rowClickHandlerCallback}
                      />
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className='tc'>
                      No Certificates
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
          {isOpen && (
            <As2CertificateModal
              open={isOpen}
              onClose={closeHandler}
              onSubmit={submitHandler}
              title='Add new AS2 Certificate'
            />
          )}
        </>
      )}
    </Box>
  )
}
